import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Course,
    Faq,
    Instructor,
    Testimonial
} from '@/types'

import { getCourses, getFaqs, getInstructors, getTestimonials } from '@/lib/courses'
import { loadPageData, REVALIDATE_SECONDS, RETRY_REVALIDATE_SECONDS } from '@/lib/page-data'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CoursesSection } from '@/components/landing/courses-section'
import { CtaSection } from '@/components/landing/cta-section'
import { FaqSection } from '@/components/landing/faq-section'
import { HeroSection } from '@/components/landing/hero-section'
import { HowItWorksSection } from '@/components/landing/how-it-works-section'
import { InstructorsSection } from '@/components/landing/instructors-section'
import { TestimonialsSection } from '@/components/landing/testimonials-section'
import { ToolsMarquee } from '@/components/landing/tools-marquee'
import { WhyUsSection } from '@/components/landing/why-us-section'

interface HomeProps {
    courses: Array<Course>
    instructors: Array<Instructor>
    testimonials: Array<Testimonial>
    faqs: Array<Faq>
}

const FEATURED_LIMIT = 6

const Home: FC<HomeProps> = ({
    courses,
    instructors,
    testimonials,
    faqs
}): ReactNode => {
    // Courses marked "featured" in the CMS appear here; without any, the first ones in catalog order do.
    const featured = courses.filter(course => course.featured)
    const highlighted = (featured.length > 0 ? featured : courses).slice(0, FEATURED_LIMIT)

    return (
        <Layout>
            <Seo />

            <HeroSection />
            <ToolsMarquee />
            <CoursesSection courses={highlighted} />
            <HowItWorksSection />
            <WhyUsSection />
            <InstructorsSection instructors={instructors} courses={courses} />
            <TestimonialsSection testimonials={testimonials} />
            <FaqSection faqs={faqs} />
            <CtaSection />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [courses, instructors, testimonials, faqs] = await Promise.all([
        loadPageData('home:courses', getCourses, []),
        loadPageData('home:instructors', getInstructors, []),
        loadPageData('home:testimonials', getTestimonials, []),
        loadPageData('home:faqs', getFaqs, [])
    ])

    const failed = courses.failed || instructors.failed || testimonials.failed || faqs.failed

    return {
        props: {
            courses: courses.data,
            instructors: instructors.data,
            testimonials: testimonials.data,
            faqs: faqs.data
        },
        revalidate: failed ? RETRY_REVALIDATE_SECONDS : REVALIDATE_SECONDS
    }
}

export default Home
